import { CdcNamingComponentType, ContractNamingComponentType, DisplayModeSelectorFormat, LabelFormat, DisplayModeSelectorVisibility, InterventionExportNamingComponentType, KeyboardShortbutBehaviour, MenuType, NavigationTreeMode, PaginationMode, SearchViewPreferencesApplyMode, SidebarCollapsible, SidebarVariant, TeacherExportNamingComponentType, Theme, ThemeVariant, MarginPreferences, HeaderHeight, KeyboardShortbutBehaviourWithFade } from "./enums";
import { Color } from "@prisma/client";
import { TableId } from "../constants/config";
import { UserPreferences } from "./types";
import { cdcDefaultUserPreferences as cdcsDefaultUserPreferences } from "@/view/cdcs-table/config/default-user-preferences";
import { contractDefaultUserPreferences } from "@/view/contracts-table/config/default-user-preferences";
import { teachersDefaultUserPreferences } from "@/view/teachers_table/config/default-user-preferences";
import { eventsDefaultUserPreferences } from "@/view/events-table/config/default-user-preferences";
import { userDefaultUserPreferences } from "@/view/administration_users/config/default-user-preferences";
import { skillsDefaultUserPreferences } from "@/view/administration_skills/config/default-user-preferences";
import { eventTypeDefaultUserPreferences } from "@/view/administration_event-types/config/default-user-preferences";
import { teacherStatusDefaultUserPreferences } from "@/view/administration_teacher-statuses/config/default-user-preferences";
import { administrationRoleDefaultUserPreferences } from "@/view/administration_roles/administration/config/default-user-preferences";
import { siteRoleDefaultUserPreferences } from "@/view/administration_roles/site/config/default-user-preferences";
import { facultyRoleDefaultUserPreferences } from "@/view/administration_roles/filiere/config/default-user-preferences";
import { siteDefaultUserPreferences } from "@/view/administration_sites/config/default-user-preferences";
import { facultyYearDefaultUserPreferences } from "@/view/faculty_semesters/config/default-user-preferences";
import { learningUnitDefaultUserPreferences } from "@/view/faculty_learning_units/config/default-user-preferences";
import {  learningUnitTreeDefaultUserPreferences } from "@/view/faculty_learning_unit_trees/config/default-user-preferences";
import { learningUnitLevelDefaultUserPreferences } from "@/view/faculty_learning_unit_levels/config/default-user-preferences";
import { promotionDefaultUserPreferences } from "@/view/faculty_promotions/config/default-user-preferences";
import { facultyRoleUserDefaultUserPreferences } from "@/view/faculty_users/config/default-user-preferences";
import { additionalFeeDefaultUserPreferences } from "@/view/site_additional-fee/config/default-user-preferences";
import { eventLocationDefaultUserPreferences } from "@/view/event_locations/config/default-user-preferences";
import { facultyDefaultUserPreferences } from "@/view/site_faculties/config/default-user-preferences";
import { siteRoleUserDefaultUserPreferences } from "@/view/site_users/config/default-user-preferences";
import { ticketDefaultUserPreferences } from "@/view/tickets_table/config/default-user-preferences";
import { cdcTemplateDefaultUserPreferences } from "@/view/cdc-templates/config/default-user-preferences";
import { contractTemplateDefaultUserPreferences } from "@/view/contract-templates/config/default-user-preferences";
import { roomsDefaultUserPreferences } from "@/view/rooms/config/default-user-preferences";

export const CURRENT_PREFERENCES_VERSION = 8;
// ============================================================================
// MARK: DEFAULT VALUES
// ============================================================================

export const DEFAULT_USER_DISPLAY_PREFERENCES: UserPreferences = {
    display: {
        theme: {
            theme: Theme.SYSTEM,
            variant: ThemeVariant.SLATE,
            accentColor: Color.neutral,
        },
        menu: {
            topbar: {
                enabled: true,
            },
            sidebar: {
                enabled: false,
                variant: SidebarVariant.SIDEBAR,
                collapsible: SidebarCollapsible.OFF_CANVAS,
                navigationTreeMode: NavigationTreeMode.WORKFLOW,
                navigationTreeAutoOpen: true,
            }
        },
        margins: MarginPreferences.WIDE,
    },
    pages: {
        filtersButtonFormat: LabelFormat.TEXT_WITH_ICON,
        sortingsButtonFormat: LabelFormat.TEXT_WITH_ICON,
        groupByButtonFormat: LabelFormat.TEXT_WITH_ICON,
        createButtonFormat: LabelFormat.TEXT_WITH_ICON,
        settingsButtonFormat: LabelFormat.ICON_ONLY,
        refreshButtonFormat: LabelFormat.ICON_ONLY,
        otherButtonsFormat: LabelFormat.TEXT_WITH_ICON,
        //
        displayModeSelectorLabelFormat: LabelFormat.ICON_ONLY,
        displayModeSelectorFormat: DisplayModeSelectorFormat.BUTTON_GROUP,
        displayModeSelectorVisibility: DisplayModeSelectorVisibility.TITLE_BAR,
        //
        headerHeight: HeaderHeight.TWO_LINES,
        displayParamsBar: true,
        //
        paginationMode: PaginationMode.PAGED,
        selectionRectangleEdgeScrollingStart: 48,
        //
        pagePreferences: {
            [TableId.events]: eventsDefaultUserPreferences,
            [TableId.cdcs]: cdcsDefaultUserPreferences,
            [TableId.cdc_templates]: cdcTemplateDefaultUserPreferences,
            [TableId.contracts]: contractDefaultUserPreferences,
            [TableId.contract_templates]: contractTemplateDefaultUserPreferences,
            [TableId.teachers]: teachersDefaultUserPreferences,
            [TableId.tickets]: ticketDefaultUserPreferences,
            //
            [TableId.administration_users]: userDefaultUserPreferences,
            [TableId.administration_event_types]: eventTypeDefaultUserPreferences,
            [TableId.administration_skills]: skillsDefaultUserPreferences,
            [TableId.administration_teacher_statuses]: teacherStatusDefaultUserPreferences,
            [TableId.administration_sites]: siteDefaultUserPreferences,
            [TableId.administration_administration_roles]: administrationRoleDefaultUserPreferences,
            [TableId.administration_site_roles]: siteRoleDefaultUserPreferences,
            [TableId.administration_faculty_roles]: facultyRoleDefaultUserPreferences,
            //
            [TableId.faculty_semesters]: facultyYearDefaultUserPreferences,
            [TableId.faculty_learning_units]: learningUnitDefaultUserPreferences,
            [TableId.faculty_learning_unit_trees]: learningUnitTreeDefaultUserPreferences,
            [TableId.faculty_learning_unit_levels]: learningUnitLevelDefaultUserPreferences,
            [TableId.faculty_promotions]: promotionDefaultUserPreferences,
            [TableId.faculty_users]: facultyRoleUserDefaultUserPreferences,
            //
            [TableId.site_fees]: additionalFeeDefaultUserPreferences,
            [TableId.event_locations]: eventLocationDefaultUserPreferences,
            [TableId.site_faculties]: facultyDefaultUserPreferences,
            [TableId.site_users]: siteRoleUserDefaultUserPreferences,
            [TableId.rooms]: roomsDefaultUserPreferences,
        },


    },
    accessibility: {
        hoverCard: {
            enabled: true,
            openOnHover: true,
            showBackground: false,
            openDelay: 500,
            animateEntryExit: true,
            animatedEntry: true,
            animatedExit: true
        },
        tooltips: {
            enabled: true,
            openDelay: 1000,
            animatedEntry: true,
            animatedExit: true
        },
        entityLinks: {
            enabled: true,
            highlightWhenEnabled: true,
            enableDelay: 500
        },
        animation: {
            enableAnimations: true,
            enableMotion: true,
            contextMenuAnimatedEntry: true,
            contextMenuAnimatedExit: true,
            popoverAnimatedEntry: true,
            popoverAnimatedExit: true,
            dialogAnimatedEntry: true,
            dialogAnimatedExit: true
        },
        navigation: {
            saveFiltersByPage: true,
            saveSortsByPage: true,
        },
        dialogConfirmations: {
            attendances_export: true,
            event_delete: true,
            event_cancel: true,
            event_attendance_cancel: true,
            cdc_delete: true,
            cdc_send: false,
            cdc_cancel_send: true,
            cdc_refuse: false,
            cdc_cancel_refuse: true,
            cdc_validate: false,
            cdc_cancel_validate: true,
            cdc_sign: false,
            cdc_cancel_sign: true,
            contract_delete: true,
            contract_sign: false,
            contract_cancel_sign: true,
            teacher_validate: false,
            teacher_cancel_validate: true,
            teacher_delete: true,
            teachers_export: true,
            user_delete: true,
            role_delete: true,
            site_delete: true,
            event_type_delete: true,
            pricing_delete: true,
            teacher_status_delete: true,
            skill_delete: true,
            event_location_delete: true,
            faculty_delete: true,
            site_user_delete: true,
            faculty_user_delete: true,
            promotion_delete: true,
            learning_unit_level_delete: true,
            learning_unit_tree_delete: true,
            learning_unit_delete: true
        },
        keyboardShortcuts: {
            GlobalSearch: { alt: false, ctrl: true, shift: false, key: "k" },
            OpenPreferences: { alt: false, ctrl: true, shift: false, key: "," },
            OpenSidebar: { alt: false, ctrl: true, shift: false, key: "b" },
            NavigateToEvents: { alt: false, ctrl: true, shift: false, key: "1" },
            NavigateToCdc: { alt: false, ctrl: true, shift: false, key: "2" },
            NavigateToContract: { alt: false, ctrl: true, shift: false, key: "3" },
            NavigateToTeacher: { alt: false, ctrl: true, shift: false, key: "4" },
            EnableHoverCard: { alt: false, ctrl: false, shift: false, key: "h" },
            EnableEntityLinks: { alt: false, ctrl: false, shift: false, key: "l" },
            LockHoverCard: { alt: false, ctrl: false, shift: false, key: "x" },
            CreateNewElement: { alt: false, ctrl: false, shift: false, key: "n" },
            OpenDisplayModeSelector: { alt: false, ctrl: false, shift: false, key: "v" },
            DisplayModeTable: { alt: false, ctrl: false, shift: false, key: "t" },
            DisplayModeGrid: { alt: false, ctrl: false, shift: false, key: "g" },
            DisplayModeList: { alt: false, ctrl: false, shift: false, key: "l" },
            DisplayModeCalendarDay: { alt: false, ctrl: false, shift: false, key: "1" },
            DisplayModeCalendarWeek: { alt: false, ctrl: false, shift: false, key: "2" },
            DisplayModeCalendarMonth: { alt: false, ctrl: false, shift: false, key: "3" },
            DisplayModeCalendarYear: { alt: false, ctrl: false, shift: false, key: "4" },
            DisplayModeTimelineResource: { alt: false, ctrl: false, shift: false, key: "5" },
            DisplayModeTimelineList: { alt: false, ctrl: false, shift: false, key: "6" },
            DisplayModeTree: { alt: false, ctrl: false, shift: false, key: "7" },
            HighlightLinks: { alt: false, ctrl: false, shift: false, key: "j" },
            HighlightTooltips: { alt: false, ctrl: false, shift: false, key: "i" },
            HighlightInteractions: { alt: false, ctrl: false, shift: false, key: "o" },
            DisplayKeyboardShortcuts: { alt: false, ctrl: false, shift: false, key: "?" },
            DisplayKeyboardShortcutsBehaviour: KeyboardShortbutBehaviourWithFade.FADE,
            EnableHoverHoverCardBehaviour: KeyboardShortbutBehaviour.TOGGLE,
            LockHoverCardBehaviour: KeyboardShortbutBehaviour.TOGGLE,
            EnableEntityLinksBehaviour: KeyboardShortbutBehaviour.HOLD,
            DisplayKeyboardShortcutsBehaviourFadeTimer: 5000,
            DisplayHelp: { alt: false, ctrl: false, shift: false, key: "F1" },
            DisplayHelpBehaviour: KeyboardShortbutBehaviourWithFade.TOGGLE,
            DisplayHelpBehaviourFadeTimer: 3000
        }
    },
    downloads: {
        cdcNaming: [
            { type: CdcNamingComponentType.CUSTOM_TEXT, value: "CDC" },
            { type: CdcNamingComponentType.TEACHER_LASTNAME },
            { type: CdcNamingComponentType.TEACHER_FIRSTNAME },
            { type: CdcNamingComponentType.START_DATE }
        ],
        contractNaming: [
            { type: ContractNamingComponentType.CUSTOM_TEXT, value: "Contrat" },
            { type: ContractNamingComponentType.TEACHER_LASTNAME },
            { type: ContractNamingComponentType.TEACHER_FIRSTNAME },
            { type: ContractNamingComponentType.START_DATE }
        ],
        interventionExportNaming: [
            { type: InterventionExportNamingComponentType.CUSTOM_TEXT, value: "Intervention" },
            { type: InterventionExportNamingComponentType.START_DATE },
            { type: InterventionExportNamingComponentType.END_DATE }
        ],
        teacherExportNaming: [
            { type: TeacherExportNamingComponentType.CUSTOM_TEXT, value: "Export" }
        ],
        dateFormat: "DD-MM-YYYY"
    },
    version: CURRENT_PREFERENCES_VERSION,
    lastUpdated: new Date().toISOString()
}
